/**
 * Image responsive avec WebP + fallback automatique.
 *
 * - Unsplash : génère srcset 800w / 1400w + format WebP via paramètres URL natifs.
 * - Customer-assets / local : utilise <img> simple avec loading=lazy.
 *   (Ces URLs ne supportent pas de transformations runtime, mais Emergent CDN
 *   les sert déjà optimisées + brotli en production.)
 *
 * Props :
 *   src         (string) — URL source
 *   alt         (string)
 *   eager       (bool)   — loading=eager + fetchPriority=high (LCP only)
 *   sizes       (string) — attribut sizes pour responsive (défaut: 100vw mobile / 50vw desktop)
 */
export default function ResponsiveImage({
  src,
  alt,
  eager = false,
  sizes = "(max-width: 768px) 100vw, 50vw",
  className = "",
  ...rest
}) {
  const isUnsplash = src.includes("images.unsplash.com");

  const loading = eager ? "eager" : "lazy";
  const fetchPriority = eager ? "high" : "auto";

  if (isUnsplash) {
    // Construit des variantes via les paramètres natifs d'Unsplash.
    // On retire les anciens & ? puis on ré-attache des params propres.
    const cleanUrl = src.split("?")[0];
    const make = (w) =>
      `${cleanUrl}?auto=format&fit=crop&w=${w}&q=70&fm=webp`;
    const fallback = `${cleanUrl}?auto=format&fit=crop&w=1400&q=70`;

    return (
      <picture>
        <source
          type="image/webp"
          srcSet={`${make(800)} 800w, ${make(1400)} 1400w`}
          sizes={sizes}
        />
        <img
          src={fallback}
          alt={alt}
          loading={loading}
          decoding="async"
          fetchPriority={fetchPriority}
          className={className}
          {...rest}
        />
      </picture>
    );
  }

  // Customer-assets / local / autres : pas de transformation côté URL,
  // on s'appuie sur le CDN d'Emergent (brotli + cache) et le lazy loading.
  return (
    <img
      src={src}
      alt={alt}
      loading={loading}
      decoding="async"
      fetchPriority={fetchPriority}
      className={className}
      {...rest}
    />
  );
}
